import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getLocalUsers, saveLocalUser, deleteLocalUser } from '../../../utils/userUtils';
import { getLocalConfigsList, loadLocalConfig } from '../../../utils/configUtils';
import UserStats from './UserStats';
import s from './UsersTab.module.sass';
import { confirmDialog } from '../../../utils/confirmDialog';

// Иконки банков для дропдауна выбора банка в модалке редактирования
const bankIconsCtx = require.context('../../../img/bankicons', false, /\.png$/);
const getBankIconUrl = (iconFileName) => {
    if (!iconFileName) return null;
    try { return bankIconsCtx(`./${iconFileName}`); } catch (_) { /* нет файла */ }
    try { return bankIconsCtx('./default-bank.png'); } catch (_) { return null; }
};

// Тарифы: цветовые маркеры вместо PNG-иконок (та же палитра,
// что и в статистике UserStats)
const TARIFF_STYLE = {
    '1': { color: '#3ECF8E', label: '1' },
    '2': { color: '#4DA3FF', label: '2' },
    '3': { color: '#7B7FF2', label: '3' },
    '4': { color: '#B48CF2', label: '4' },
    'VIP': { color: '#E7B008', label: 'VIP' },
};

const getTariffStyle = (raw) => {
    const norm = (raw || '').replace(/[💰❗⚠️\s]/g, '').toUpperCase();
    if (norm.includes('VIP') || norm.includes('ВИП')) return TARIFF_STYLE.VIP;
    const digit = (norm.match(/[1-4]/) || [])[0];
    if (digit && TARIFF_STYLE[digit]) return TARIFF_STYLE[digit];
    return { color: '#8B93A7', label: '—' };
};

// Цветной бейдж тарифа (число или VIP в круге)
const TariffBadge = ({ tariff }) => {
    const st = getTariffStyle(tariff);
    return (
        <span
            className={s.tariffBadge}
            style={{ color: st.color, background: `${st.color}22`, borderColor: `${st.color}66` }}
        >
            {st.label}
        </span>
    );
};

const UsersTab = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedConfigs, setSelectedConfigs] = useState([]); // Фильтр по конфигам
    const [selectedTariffs, setSelectedTariffs] = useState([]); // Фильтр по тарифам
    const [dateSortOrder, setDateSortOrder] = useState('desc'); // asc, desc - сортировка по дате
    const [showConfigDropdown, setShowConfigDropdown] = useState(false);
    const [showTariffDropdown, setShowTariffDropdown] = useState(false);
    const [showItemsPerPageDropdown, setShowItemsPerPageDropdown] = useState(false);
    const [showCheckpointDropdown, setShowCheckpointDropdown] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [availableConfigs, setAvailableConfigs] = useState([]);
    const [availableTariffs, setAvailableTariffs] = useState([]);
    // Банки и тарифы из конфига редактируемого пользователя
    const [editBanks, setEditBanks] = useState([]);
    const [editTariffs, setEditTariffs] = useState([]);
    const [showBankDropdown, setShowBankDropdown] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        cryptoAddress: '',
        telegram: '',
        tariff: '',
        selectedBank: '',
        checkpoint: 1
    });
    const checkpointOptions = [
        { value: 1, label: '1 - Регистрация' },
        { value: 2, label: '2 - Успех' },
        { value: 3, label: '3 - Действия (комиссия, верификация и т.д.)' },
        { value: 4, label: '4 - Продолжение действий' },
        { value: 5, label: '5 - Продолжение действий' },
        { value: 6, label: '6 - Продолжение действий' },
        { value: 7, label: '7 - Продолжение действий' },
        { value: 8, label: '8 - Продолжение действий' }
    ];

    const getSentLogForManualCheckpoint = (sentLog = {}, checkpoint = 1) => {
        if (checkpoint <= 1) {
            return {};
        }

        const nextLog = {};

        if (sentLog.registration) {
            nextLog.registration = sentLog.registration;
        }

        if (checkpoint >= 3 && sentLog.successFirst) {
            nextLog.successFirst = sentLog.successFirst;
        }

        return nextLog;
    };

    const loadConfigs = async () => {
        try {
            const configs = await getLocalConfigsList();
            setAvailableConfigs(configs);
        } catch (err) {
            console.error('Ошибка загрузки конфигов:', err);
            setAvailableConfigs([]);
        }
    };

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const loadedUsers = await getLocalUsers();
            setUsers(loadedUsers);
        } catch (err) {
            console.error('Ошибка загрузки пользователей:', err);
            setError('Не удалось загрузить пользователей');
        } finally {
            setLoading(false);
        }
    };

    // Закрываем дропдауны при клике вне их
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest(`.${s.dropdownWrapper}`) && !e.target.closest(`.${s.filterWrapper}`)) {
                setShowConfigDropdown(false);
                setShowTariffDropdown(false);
                setShowItemsPerPageDropdown(false);
                setShowCheckpointDropdown(false);
                setShowBankDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, []);

    useEffect(() => {
        loadUsers();
        loadConfigs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Обновляем список доступных тарифов при загрузке пользователей
        const tariffs = [...new Set(users.map(u => u.tariff).filter(Boolean))].sort();
        setAvailableTariffs(tariffs);
    }, [users]);

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('ru-RU', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'Europe/Moscow'
            }).format(date);
        } catch {
            return dateString;
        }
    };

    // Функция для получения текста тарифа без эмодзи
    const getTariffText = (tariffLabel) => {
        if (!tariffLabel) return '—';
        // Убираем эмодзи, оставляем только текст
        return tariffLabel.replace(/[💰❗⚠️]/g, '').trim();
    };

    const filteredAndSortedUsers = users
        .filter(user => {
            // Фильтр по поисковому запросу
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const matchesSearch = (
                    (user.name || '').toLowerCase().includes(query) ||
                    (user.email || '').toLowerCase().includes(query) ||
                    (user.cryptoAddress || '').toLowerCase().includes(query) ||
                    (user.telegram || '').toLowerCase().includes(query) ||
                    (user.selectedBank || '').toLowerCase().includes(query) ||
                    (user.tariff || '').toLowerCase().includes(query) ||
                    (user.createdBy || '').toLowerCase().includes(query) ||
                    (user.updatedBy || '').toLowerCase().includes(query) ||
                    (user.configFile || '').toLowerCase().includes(query)
                );
                if (!matchesSearch) return false;
            }

            // Фильтр по конфигам
            if (selectedConfigs.length > 0) {
                const userConfig = user.configFile || '';
                const matchesConfig = selectedConfigs.some(config => {
                    const configFilename = typeof config === 'string' ? config : config.filename || config.name;
                    return userConfig === configFilename || userConfig === configFilename + '.json' || userConfig + '.json' === configFilename;
                });
                if (!matchesConfig) return false;
            }

            // Фильтр по тарифам
            if (selectedTariffs.length > 0) {
                const userTariff = user.tariff || '';
                if (!selectedTariffs.includes(userTariff)) return false;
            }

            return true;
        })
        .sort((a, b) => {
            // Сортировка только по дате
            const aValue = new Date(a.timestamp || 0).getTime() || 0;
            const bValue = new Date(b.timestamp || 0).getTime() || 0;
            
            if (dateSortOrder === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });

    // Пагинация
    const hasActiveFilters = Boolean(searchQuery.trim()) || selectedConfigs.length > 0 || selectedTariffs.length > 0;
    const filteredUsersCount = filteredAndSortedUsers.length;
    const totalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    let paginatedUsers = filteredAndSortedUsers.slice(startIndex, endIndex);
    
    // Если в последней строке меньше 3 карточек и есть следующая страница,
    // добавляем карточки со следующей страницы, чтобы заполнить строку
    const itemsInLastRow = paginatedUsers.length % 3;
    if (itemsInLastRow > 0 && itemsInLastRow < 3 && currentPage < totalPages) {
        const additionalItems = 3 - itemsInLastRow;
        const nextPageStart = endIndex;
        const nextPageEnd = Math.min(nextPageStart + additionalItems, filteredAndSortedUsers.length);
        const additionalUsers = filteredAndSortedUsers.slice(nextPageStart, nextPageEnd);
        paginatedUsers = [...paginatedUsers, ...additionalUsers];
    }

    // Сброс на первую страницу при изменении фильтров или поиска
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, itemsPerPage, selectedConfigs, selectedTariffs, dateSortOrder]);

    const handleEdit = (user) => {
        setEditingUser(user);
        setShowCheckpointDropdown(false);
        setShowBankDropdown(false);
        setFormData({
            name: user.name || '',
            email: user.email || '',
            cryptoAddress: user.cryptoAddress || '',
            telegram: user.telegram || '',
            tariff: user.tariff || '',
            selectedBank: user.selectedBank || '',
            checkpoint: user.checkpoint || 1
        });
        setIsModalOpen(true);

        // Подтягиваем банки и тарифы из конфига, которым создан юзер
        setEditBanks([]);
        setEditTariffs([]);
        if (user.configFile) {
            loadLocalConfig(user.configFile).then((cfg) => {
                const banksObj = (cfg && cfg.banks) || {};
                const banks = Object.entries(banksObj)
                    .filter(([, v]) => v && v.enabled)
                    .sort((a, b) => (a[1].priority ?? 999) - (b[1].priority ?? 999))
                    .map(([name, v]) => ({ name, iconFileName: v.iconFileName }));
                setEditBanks(banks);
                const tSource = (cfg && (cfg.tariffBaseAmounts || cfg.tariffDepositAmounts)) || {};
                setEditTariffs(Object.keys(tSource));
            }).catch(() => {
                setEditBanks([]);
                setEditTariffs([]);
            });
        }
    };

    const handleDelete = async (userId) => {
        console.log('[DELETE] Начало удаления пользователя, ID:', userId, 'Тип:', typeof userId);
        
        if (!(await confirmDialog({
            title: 'Удалить пользователя?',
            message: 'Запись пользователя будет удалена из базы навсегда — вместе с его данными и прогрессом.',
            confirmText: 'Удалить',
            cancelText: 'Оставить',
            confirmHint: 'Безвозвратно удалит пользователя из базы',
            cancelHint: 'Закроет окно, пользователь останется'
        }))) {
            return;
        }

        try {
            setError(null);
            const result = await deleteLocalUser(userId);
            console.log('[DELETE] Результат удаления:', result);
            if (result.success) {
                await loadUsers();
                console.log('[DELETE] Список пользователей обновлен');
            } else {
                setError('Не удалось удалить пользователя: ' + (result.error || 'Неизвестная ошибка'));
            }
        } catch (err) {
            console.error('[DELETE] Ошибка удаления пользователя:', err);
            setError('Не удалось удалить пользователя: ' + err.message);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        
        if (!editingUser) return;

        try {
            setError(null);
            const nextCheckpoint = formData.checkpoint || 1;
            const previousCheckpoint = editingUser.checkpoint || 1;
            const sentLog = nextCheckpoint === previousCheckpoint
                ? (editingUser.sentLog || {})
                : getSentLogForManualCheckpoint(editingUser.sentLog || {}, nextCheckpoint);

            const result = await saveLocalUser({
                mode: 'update',
                id: editingUser.id,
                ...formData,
                checkpoint: nextCheckpoint,
                sentLog,
                configFile: editingUser.configFile || null,
                timestamp: editingUser.timestamp // Сохраняем оригинальную дату регистрации
            });

            if (result.success) {
                await loadUsers();
                setIsModalOpen(false);
                setEditingUser(null);
                setFormData({
                    name: '',
                    email: '',
                    cryptoAddress: '',
                    telegram: '',
                    tariff: '',
                    selectedBank: '',
                    checkpoint: 1
                });
            } else {
                setError('Не удалось сохранить изменения: ' + (result.error || 'Неизвестная ошибка'));
            }
        } catch (err) {
            console.error('Ошибка сохранения пользователя:', err);
            setError('Не удалось сохранить изменения: ' + err.message);
        }
    };

    const handleConfigToggle = (configFilename) => {
        setSelectedConfigs(prev => {
            if (prev.includes(configFilename)) {
                return prev.filter(c => c !== configFilename);
            } else {
                return [...prev, configFilename];
            }
        });
    };

    const handleTariffToggle = (tariff) => {
        setSelectedTariffs(prev => {
            if (prev.includes(tariff)) {
                return prev.filter(t => t !== tariff);
            } else {
                return [...prev, tariff];
            }
        });
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setShowCheckpointDropdown(false);
        setShowBankDropdown(false);
        setEditingUser(null);
        setFormData({
            name: '',
            email: '',
            cryptoAddress: '',
            telegram: '',
            tariff: '',
            selectedBank: '',
            checkpoint: 1
        });
        setError(null);
    };

    if (loading) {
        return (
            <div className={s.loading}>
                <div className={s.spinner}></div>
                <p>Загрузка пользователей...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={s.error}>
                <p>{error}</p>
                <button onClick={loadUsers} className={s.retryButton}>
                    <svg className={s.buttonIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23 4 23 10 17 10"></polyline>
                        <polyline points="1 20 1 14 7 14"></polyline>
                        <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10"></path>
                        <path d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14"></path>
                    </svg>
                    <span>Повторить</span>
                </button>
            </div>
        );
    }

    return (
        <div className={s.usersTab}>
            <UserStats />
            
            <section className={s.panel}>
                <header className={s.panelHead}>
                    <div className={s.panelHeadMain}>
                        <span className={s.panelIcon}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                        </span>
                        <div className={s.panelHeadText}>
                            <h2 className={s.panelTitle}>Пользователи</h2>
                            <p className={s.panelSub}>Список, фильтры и редактирование записей</p>
                        </div>
                    </div>
                    <div className={s.stats}>
                        <span className={s.statItem}>
                            <span className={s.statIcon}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                </svg>
                            </span>
                            <span>Всего: <strong>{filteredUsersCount}</strong></span>
                        </span>
                        {hasActiveFilters && (
                            <span className={s.statItem}>
                                <span className={s.statIcon}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8" />
                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                </span>
                                <span>В базе: <strong>{users.length}</strong></span>
                            </span>
                        )}
                        {filteredAndSortedUsers.length > 0 && (
                            <span className={s.statItem}>
                                <span className={s.statIcon}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="16" rx="2" />
                                        <line x1="7" y1="8" x2="17" y2="8" />
                                        <line x1="7" y1="12" x2="15" y2="12" />
                                        <line x1="7" y1="16" x2="13" y2="16" />
                                    </svg>
                                </span>
                                <span>Страница: <strong>{currentPage}</strong> из <strong>{totalPages}</strong></span>
                            </span>
                        )}
                    </div>
                </header>

                <div className={s.controls}>
                    <div className={s.searchContainer}>
                        <svg className={s.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            type="text"
                            className={s.searchInput}
                            placeholder="Поиск по имени, email, адресу, телеграм, саппу..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className={s.searchClear} onClick={() => setSearchQuery('')} title="Очистить">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        )}
                    </div>

                    <div className={s.toolbar}>
                        <div className={s.toolGroup}>
                    <div className={s.dropdownWrapper}>
                            <button
                                className={`${s.dropdownButton} ${selectedConfigs.length > 0 ? s.dropdownActive : ''}`}
                                title="Фильтр по конфигу"
                                onClick={() => {
                                    setShowConfigDropdown(!showConfigDropdown);
                                    setShowTariffDropdown(false);
                                    setShowItemsPerPageDropdown(false);
                                }}
                            >
                                <svg className={s.ddIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                <span>
                                    {selectedConfigs.length === 0 
                                        ? 'Все конфиги' 
                                        : selectedConfigs.length === 1
                                            ? (availableConfigs.find(c => (c.filename || c.name || c) === selectedConfigs[0])?.name || selectedConfigs[0].replace('.json', ''))
                                            : `Конфигов: ${selectedConfigs.length}`
                                    }
                                </span>
                                <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points={showConfigDropdown ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}></polyline>
                                </svg>
                            </button>
                            {showConfigDropdown && (
                                <div className={s.dropdownMenu}>
                                    <div className={s.dropdownHeader}>
                                        <span>Выберите конфиги</span>
                                        {selectedConfigs.length > 0 && (
                                            <button
                                                className={s.clearAllButton}
                                                onClick={() => {
                                                    setSelectedConfigs([]);
                                                }}
                                            >
                                                <svg className={s.buttonIcon} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                                </svg>
                                                <span>Очистить</span>
                                            </button>
                                        )}
                                    </div>
                                    <div className={s.checkboxList}>
                                        {availableConfigs.map((config) => {
                                            const configFilename = config.filename || config.name || config;
                                            const configName = config.name || configFilename.replace('.json', '') || configFilename;
                                            const isSelected = selectedConfigs.includes(configFilename);
                                            return (
                                                <label key={configFilename} className={s.checkboxItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleConfigToggle(configFilename)}
                                                    />
                                                    <span>{configName}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                    </div>

                    <div className={s.dropdownWrapper}>
                            <button
                                className={`${s.dropdownButton} ${selectedTariffs.length > 0 ? s.dropdownActive : ''}`}
                                title="Фильтр по тарифу"
                                onClick={() => {
                                    setShowTariffDropdown(!showTariffDropdown);
                                    setShowConfigDropdown(false);
                                    setShowItemsPerPageDropdown(false);
                                }}
                            >
                                <svg className={s.ddIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 7h-9" />
                                    <path d="M14 17H5" />
                                    <circle cx="17" cy="17" r="3" />
                                    <circle cx="7" cy="7" r="3" />
                                </svg>
                                <span>
                                    {selectedTariffs.length === 0 
                                        ? 'Все тарифы' 
                                        : selectedTariffs.length === 1
                                            ? getTariffText(selectedTariffs[0])
                                            : `Тарифов: ${selectedTariffs.length}`
                                    }
                                </span>
                                <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points={showTariffDropdown ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}></polyline>
                                </svg>
                            </button>
                            {showTariffDropdown && (
                                <div className={s.dropdownMenu}>
                                    <div className={s.dropdownHeader}>
                                        <span>Выберите тарифы</span>
                                        {selectedTariffs.length > 0 && (
                                            <button
                                                className={s.clearAllButton}
                                                onClick={() => {
                                                    setSelectedTariffs([]);
                                                }}
                                            >
                                                <svg className={s.buttonIcon} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                                </svg>
                                                <span>Очистить</span>
                                            </button>
                                        )}
                                    </div>
                                    <div className={s.checkboxList}>
                                        {availableTariffs.map((tariff) => {
                                            const isSelected = selectedTariffs.includes(tariff);
                                            return (
                                                <label key={tariff} className={s.checkboxItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleTariffToggle(tariff)}
                                                    />
                                                    <span className={s.tariffWithIcon}>
                                                        <TariffBadge tariff={tariff} />
                                                        <span>{getTariffText(tariff)}</span>
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                    </div>

                    <button
                        className={s.sortButton}
                        onClick={() => setDateSortOrder(dateSortOrder === 'asc' ? 'desc' : 'asc')}
                        title="Сортировка по дате регистрации"
                    >
                        <svg className={s.ddIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 5h10" />
                            <path d="M11 9h7" />
                            <path d="M11 13h4" />
                            <path d="m3 17 3 3 3-3" />
                            <path d="M6 18V4" />
                        </svg>
                        <span>{dateSortOrder === 'asc' ? 'Сначала ранние' : 'Сначала поздние'}</span>
                    </button>
                        </div>

                        <div className={s.toolGroup}>
                            <span className={s.toolMiniLabel}>Показывать</span>
                        <div className={s.dropdownWrapper}>
                            <button
                                className={s.itemsPerPageButton}
                                onClick={() => {
                                    setShowItemsPerPageDropdown(!showItemsPerPageDropdown);
                                    setShowConfigDropdown(false);
                                    setShowTariffDropdown(false);
                                }}
                            >
                                <span>{itemsPerPage}</span>
                                <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points={showItemsPerPageDropdown ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}></polyline>
                                </svg>
                            </button>
                            {showItemsPerPageDropdown && (
                                <div className={`${s.dropdownMenu} ${s.dropdownMenuRight}`}>
                                    <div className={s.checkboxList}>
                                        {[10, 25, 50, 100].map((option) => (
                                            <button
                                                key={option}
                                                type="button"
                                                className={`${s.dropdownOptionButton} ${itemsPerPage === option ? s.active : ''}`}
                                                onClick={() => {
                                                    setItemsPerPage(option);
                                                    setShowItemsPerPageDropdown(false);
                                                }}
                                            >
                                                <span>{option}</span>
                                                {itemsPerPage === option && <span className={s.dropdownOptionCheck}>✓</span>}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <button onClick={loadUsers} className={s.refreshButton}>
                            <svg className={s.buttonIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="23 4 23 10 17 10"></polyline>
                                <polyline points="1 20 1 14 7 14"></polyline>
                                <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10"></path>
                                <path d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14"></path>
                            </svg>
                            <span>Обновить</span>
                        </button>
                        </div>
                    </div>
                </div>

                <div className={s.panelBody}>
            {filteredAndSortedUsers.length === 0 ? (
                <div className={s.empty}>
                    {searchQuery ? (
                        <>
                            <p>Пользователи не найдены</p>
                            <button onClick={() => setSearchQuery('')} className={s.clearButton}>
                                <svg className={s.buttonIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                                <span>Очистить поиск</span>
                            </button>
                        </>
                    ) : (
                        <p>Пользователи не найдены</p>
                    )}
                </div>
            ) : (
                <>
                    <div className={s.listHeader}>
                        <h3 className={s.listTitle}>
                            <span className={s.titleIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7" />
                                    <rect x="14" y="3" width="7" height="7" />
                                    <rect x="14" y="14" width="7" height="7" />
                                    <rect x="3" y="14" width="7" height="7" />
                                </svg>
                            </span>
                            <span>Карточки пользователей</span>
                        </h3>
                        <span className={s.listMeta}>Показано: {paginatedUsers.length}</span>
                    </div>
                    <div className={s.usersGrid}>
                        {paginatedUsers.map((user) => {
                            const userIdStr = user.id ? String(user.id) : null;
                            const configName = user.configFile ? user.configFile.replace('.json', '') : null;
                            const sentCount = user.sentLog ? Object.values(user.sentLog).filter(log => log && log.sent === true).length : 0;
                            const initial = (user.name || 'U').trim()[0]?.toUpperCase() || 'U';
                            return (
                            <div key={userIdStr || `user-${Math.random()}`} className={s.userCard}>
                                <div className={s.cardHeader}>
                                    <div className={s.userAvatar}>{initial}</div>
                                    <div className={s.headMain}>
                                        <h3 className={s.userName} title={user.name || ''}>{user.name || 'Без имени'}</h3>
                                        <div className={s.headChips}>
                                            {configName && (
                                                <span className={s.userConfig} title={configName}>{configName}</span>
                                            )}
                                            {user.tariff && (
                                                <span className={s.tariffChip}>
                                                    <TariffBadge tariff={user.tariff} />
                                                    <span>{getTariffText(user.tariff)}</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className={s.headActions}>
                                        <button onClick={() => handleEdit(user)} className={s.iconEdit} title="Редактировать">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                        </button>
                                        <button onClick={() => handleDelete(user.id)} className={s.iconDelete} title="Удалить">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <div className={s.cardBody}>
                                    <div className={s.contactGrid}>
                                        <div className={`${s.field} ${s.fieldWide}`}>
                                            <span className={s.fieldLabel}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2"></path>
                                                    <path d="M22 6v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6"></path>
                                                    <path d="m3 7 9 6 9-6"></path>
                                                </svg>
                                                <span>Email</span>
                                            </span>
                                            <span className={s.fieldValue} title={user.email || ''}>{user.email || '—'}</span>
                                        </div>

                                        <div className={`${s.field} ${s.fieldWide}`}>
                                            <span className={s.fieldLabel}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="3" y1="21" x2="21" y2="21"></line>
                                                    <path d="M3 10h18"></path>
                                                    <path d="M5 10V21"></path>
                                                    <path d="M19 10V21"></path>
                                                    <path d="M9 10V21"></path>
                                                    <path d="M15 10V21"></path>
                                                    <path d="M12 3 21 7v3H3V7z"></path>
                                                </svg>
                                                <span>Счёт</span>
                                            </span>
                                            <span className={`${s.fieldValue} ${s.fieldMono}`} title={user.cryptoAddress || ''}>{user.cryptoAddress || '—'}</span>
                                        </div>

                                        <div className={s.field}>
                                            <span className={s.fieldLabel}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 2 11 13"></path>
                                                    <path d="M22 2 15 22 11 13 2 9 22 2z"></path>
                                                </svg>
                                                <span>Телеграм</span>
                                            </span>
                                            <span className={s.fieldValue} title={user.telegram || ''}>{user.telegram || '—'}</span>
                                        </div>

                                        <div className={s.field}>
                                            <span className={s.fieldLabel}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M20 7h-9"></path>
                                                    <path d="M14 17H5"></path>
                                                    <circle cx="17" cy="17" r="3"></circle>
                                                    <circle cx="7" cy="7" r="3"></circle>
                                                </svg>
                                                <span>Банк</span>
                                            </span>
                                            <span className={s.fieldValue} title={user.selectedBank || ''}>{user.selectedBank || '—'}</span>
                                        </div>
                                    </div>

                                    <div className={s.metaStrip}>
                                        <span className={s.metaChip} title="Дата регистрации">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="4" width="18" height="18" rx="2"></rect>
                                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                                <line x1="3" y1="10" x2="21" y2="10"></line>
                                            </svg>
                                            <span>{formatDate(user.timestamp)}</span>
                                        </span>
                                        {user.createdBy && (
                                            <span className={s.metaChip} title="Создан саппом">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M16 21v-2a4 4 0 0 0-4-4H4a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="8" cy="7" r="4"></circle>
                                                </svg>
                                                <span>{user.createdBy}</span>
                                            </span>
                                        )}
                                        {user.updatedBy && (
                                            <span className={s.metaChip} title="Обновлён саппом">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="23 4 23 10 17 10"></polyline>
                                                    <polyline points="1 20 1 14 7 14"></polyline>
                                                    <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10"></path>
                                                    <path d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14"></path>
                                                </svg>
                                                <span>{user.updatedBy}</span>
                                            </span>
                                        )}
                                        {sentCount > 0 && (
                                            <span className={`${s.metaChip} ${s.metaChipSent}`} title="Отправлено сообщений">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 2 11 13"></path>
                                                    <path d="M22 2 15 22 11 13 2 9 22 2z"></path>
                                                </svg>
                                                <span>{sentCount}</span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                        })}
                    </div>
                    
                    {totalPages > 1 && (
                        <div className={s.pagination}>
                            <button
                                className={`${s.pageButton} ${currentPage === 1 ? s.disabled : ''}`}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                            >
                                <svg className={s.buttonIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="15 18 9 12 15 6"></polyline>
                                </svg>
                                <span>Назад</span>
                            </button>
                            
                            <div className={s.pageNumbers}>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(page => {
                                        // Показываем первую, последнюю, текущую и соседние страницы
                                        if (page === 1 || page === totalPages) return true;
                                        if (Math.abs(page - currentPage) <= 1) return true;
                                        return false;
                                    })
                                    .map((page, index, array) => {
                                        // Добавляем многоточие между разрывами
                                        const prevPage = array[index - 1];
                                        const showEllipsis = prevPage && page - prevPage > 1;
                                        
                                        return (
                                            <React.Fragment key={page}>
                                                {showEllipsis && <span className={s.ellipsis}>...</span>}
                                                <button
                                                    className={`${s.pageNumber} ${currentPage === page ? s.active : ''}`}
                                                    onClick={() => setCurrentPage(page)}
                                                >
                                                    {page}
                                                </button>
                                            </React.Fragment>
                                        );
                                    })}
                            </div>
                            
                            <button
                                className={`${s.pageButton} ${currentPage === totalPages ? s.disabled : ''}`}
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                            >
                                <span>Вперед</span>
                                <svg className={s.buttonIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </button>
                        </div>
                    )}
                </>
            )}
                </div>
            </section>

            {/* Модальное окно редактирования */}
            {isModalOpen && ReactDOM.createPortal(
                (() => {
                    const initial = (formData.name || 'U').trim()[0]?.toUpperCase() || 'U';
                    const editConfigName = editingUser?.configFile ? editingUser.configFile.replace('.json', '') : null;
                    const selectedCp = checkpointOptions.find(o => o.value === formData.checkpoint);
                    const cpDesc = selectedCp ? (selectedCp.label.split(' - ')[1] || selectedCp.label) : '';
                    // Тарифы из конфига + текущий (если его нет в списке — не теряем)
                    const tariffChoices = (formData.tariff && !editTariffs.includes(formData.tariff))
                        ? [formData.tariff, ...editTariffs]
                        : editTariffs;
                    const currentBank = editBanks.find(b => b.name === formData.selectedBank);
                    return (
                <div
                    className={s.modalOverlay}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}
                >
                    <div className={s.modalContent} onMouseDown={(e) => e.stopPropagation()}>
                        <div className={s.modalHeader}>
                            <div className={s.modalHeaderText}>
                                <h3 className={s.modalTitle}>
                                    <span className={s.modalTitleIcon}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                        </svg>
                                    </span>
                                    <span>Редактирование пользователя</span>
                                </h3>
                                <p className={s.modalSubtitle}>Измените данные и этап воронки, затем сохраните</p>
                            </div>
                            <button className={s.modalClose} onClick={handleCloseModal}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSave} className={s.modalForm}>
                            {error && <div className={s.errorMessage}>{error}</div>}

                            {/* Контекст: кого редактируем */}
                            <div className={s.editSummary}>
                                <div className={s.editAvatar}>{initial}</div>
                                <div className={s.editSummaryMain}>
                                    <div className={s.editSummaryName}>{formData.name || 'Без имени'}</div>
                                    <div className={s.editSummaryChips}>
                                        {editConfigName && (
                                            <span className={s.userConfig} title={editConfigName}>{editConfigName}</span>
                                        )}
                                        {formData.tariff && (
                                            <span className={s.tariffChip}>
                                                <TariffBadge tariff={formData.tariff} />
                                                <span>{getTariffText(formData.tariff)}</span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {editingUser?.timestamp && (
                                    <div className={s.editSummaryMeta}>
                                        <span className={s.editMetaChip} title="Дата регистрации">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="4" width="18" height="18" rx="2"></rect>
                                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                                <line x1="3" y1="10" x2="21" y2="10"></line>
                                            </svg>
                                            <span>{formatDate(editingUser.timestamp)}</span>
                                        </span>
                                        {editingUser.createdBy && (
                                            <span className={s.editMetaChip} title="Создан саппом">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M16 21v-2a4 4 0 0 0-4-4H4a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="8" cy="7" r="4"></circle>
                                                </svg>
                                                <span>{editingUser.createdBy}</span>
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className={s.formSection}>
                                <div className={s.formSectionTitle}>
                                    <span className={s.formSectionIcon}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                    </span>
                                    <span>Контактные данные</span>
                                </div>
                                <div className={s.formGrid}>
                                    <div className={`${s.formGroup} ${s.formGroupWide}`}>
                                        <label className={s.modalFieldLabel}>
                                            <span className={s.labelIcon}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="12" cy="7" r="4"></circle>
                                                </svg>
                                            </span>
                                            <span>Имя</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={s.input}
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Введите имя"
                                        />
                                    </div>

                                    <div className={s.formGroup}>
                                        <label className={s.modalFieldLabel}>
                                            <span className={s.labelIcon}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2"></path>
                                                    <path d="M22 6v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6"></path>
                                                    <path d="m3 7 9 6 9-6"></path>
                                                </svg>
                                            </span>
                                            <span>Email</span>
                                        </label>
                                        <input
                                            type="email"
                                            className={s.input}
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="Введите email"
                                        />
                                    </div>

                                    <div className={s.formGroup}>
                                        <label className={s.modalFieldLabel}>
                                            <span className={s.labelIcon}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 2 11 13"></path>
                                                    <path d="M22 2 15 22 11 13 2 9 22 2z"></path>
                                                </svg>
                                            </span>
                                            <span>Телеграм</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={s.input}
                                            value={formData.telegram}
                                            onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
                                            placeholder="Введите телеграм"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={s.formSection}>
                                <div className={s.formSectionTitle}>
                                    <span className={s.formSectionIcon}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="3" y1="21" x2="21" y2="21"></line>
                                            <path d="M3 10h18"></path>
                                            <path d="M5 10V21M19 10V21M9 10V21M15 10V21"></path>
                                            <path d="M12 3 21 7v3H3V7z"></path>
                                        </svg>
                                    </span>
                                    <span>Платёжные реквизиты</span>
                                </div>
                                <div className={s.formGrid}>
                                    <div className={`${s.formGroup} ${s.formGroupWide}`}>
                                        <label className={s.modalFieldLabel}>
                                            <span className={s.labelIcon}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="3" y1="21" x2="21" y2="21"></line>
                                                    <path d="M3 10h18"></path>
                                                    <path d="M5 10V21M19 10V21M9 10V21M15 10V21"></path>
                                                    <path d="M12 3 21 7v3H3V7z"></path>
                                                </svg>
                                            </span>
                                            <span>Счёт</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={s.input}
                                            value={formData.cryptoAddress}
                                            onChange={(e) => setFormData({ ...formData, cryptoAddress: e.target.value })}
                                            placeholder="Введите счёт"
                                        />
                                    </div>

                                    <div className={s.formGroup}>
                                        <label className={s.modalFieldLabel}>
                                            <span className={s.labelIcon}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="5" y="2" width="14" height="20" rx="2"></rect>
                                                    <line x1="9" y1="6" x2="9" y2="8"></line>
                                                    <line x1="15" y1="6" x2="15" y2="8"></line>
                                                    <line x1="9" y1="12" x2="9" y2="14"></line>
                                                    <line x1="15" y1="12" x2="15" y2="14"></line>
                                                    <line x1="5" y1="10" x2="19" y2="10"></line>
                                                </svg>
                                            </span>
                                            <span>Банк</span>
                                        </label>
                                        {editBanks.length > 0 ? (
                                            <div className={s.dropdownWrapper}>
                                                <button
                                                    type="button"
                                                    className={s.modalDropdownButton}
                                                    onClick={() => { setShowBankDropdown(!showBankDropdown); setShowCheckpointDropdown(false); }}
                                                >
                                                    <span className={s.bankTrigger}>
                                                        {currentBank && getBankIconUrl(currentBank.iconFileName) && (
                                                            <img className={s.bankOptIcon} src={getBankIconUrl(currentBank.iconFileName)} alt="" />
                                                        )}
                                                        <span className={s.bankTriggerText}>{formData.selectedBank || 'Выберите банк'}</span>
                                                    </span>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points={showBankDropdown ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}></polyline>
                                                    </svg>
                                                </button>
                                                {showBankDropdown && (
                                                    <div className={s.dropdownMenu}>
                                                        <div className={`${s.checkboxList} ${s.bankList}`}>
                                                            {editBanks.map((bank) => {
                                                                const iconUrl = getBankIconUrl(bank.iconFileName);
                                                                const isSel = formData.selectedBank === bank.name;
                                                                return (
                                                                    <button
                                                                        key={bank.name}
                                                                        type="button"
                                                                        className={`${s.dropdownOptionButton} ${isSel ? s.active : ''}`}
                                                                        onClick={() => {
                                                                            setFormData({ ...formData, selectedBank: bank.name });
                                                                            setShowBankDropdown(false);
                                                                        }}
                                                                    >
                                                                        <span className={s.bankOption}>
                                                                            {iconUrl && <img className={s.bankOptIcon} src={iconUrl} alt="" />}
                                                                            <span>{bank.name}</span>
                                                                        </span>
                                                                        {isSel && <span className={s.dropdownOptionCheck}>✓</span>}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <input
                                                type="text"
                                                className={s.input}
                                                value={formData.selectedBank}
                                                onChange={(e) => setFormData({ ...formData, selectedBank: e.target.value })}
                                                placeholder="Введите банк"
                                            />
                                        )}
                                    </div>

                                    <div className={s.formGroup}>
                                        <label className={s.modalFieldLabel}>
                                            <span className={s.labelIcon}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M20 7h-9"></path>
                                                    <path d="M14 17H5"></path>
                                                    <circle cx="17" cy="17" r="3"></circle>
                                                    <circle cx="7" cy="7" r="3"></circle>
                                                </svg>
                                            </span>
                                            <span>Тариф</span>
                                        </label>
                                        {tariffChoices.length > 0 ? (
                                            <div className={s.tariffChoices}>
                                                {tariffChoices.map((t) => (
                                                    <button
                                                        key={t}
                                                        type="button"
                                                        className={`${s.tariffChoice} ${formData.tariff === t ? s.tariffChoiceActive : ''}`}
                                                        onClick={() => setFormData({ ...formData, tariff: t })}
                                                        title={getTariffText(t)}
                                                    >
                                                        <TariffBadge tariff={t} />
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className={s.tariffInputRow}>
                                                {formData.tariff && <TariffBadge tariff={formData.tariff} />}
                                                <input
                                                    type="text"
                                                    className={s.input}
                                                    value={formData.tariff}
                                                    onChange={(e) => setFormData({ ...formData, tariff: e.target.value })}
                                                    placeholder="Введите тариф"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={s.formSection}>
                                <div className={s.formSectionTitle}>
                                    <span className={s.formSectionIcon}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                                        </svg>
                                    </span>
                                    <span>Этап воронки</span>
                                </div>
                                <div className={s.checkpointPicker}>
                                    {checkpointOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            className={`${s.checkpointPill} ${formData.checkpoint === option.value ? s.checkpointPillActive : ''}`}
                                            onClick={() => setFormData({ ...formData, checkpoint: option.value })}
                                            title={option.label}
                                        >
                                            {option.value}
                                        </button>
                                    ))}
                                </div>
                                <div className={s.checkpointDesc}>
                                    <span className={s.checkpointDescNum}>{formData.checkpoint}</span>
                                    <span>{cpDesc}</span>
                                </div>
                                <small className={s.helpText}>
                                    Этап определяет, какие кнопки отправки писем будут активны для пользователя. Смена этапа вручную сбросит отметки об отправленных письмах выше нового этапа.
                                </small>
                            </div>

                            <div className={s.modalActions}>
                                <button type="button" onClick={handleCloseModal} className={s.cancelButton}>
                                    <span>Отмена</span>
                                </button>
                                <button type="submit" className={s.submitButton}>
                                    <svg className={s.buttonIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                                        <polyline points="7 3 7 8 15 8"></polyline>
                                    </svg>
                                    <span>Сохранить изменения</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                    );
                })(),
                document.body
            )}
        </div>
    );
};

export default UsersTab;

